import {
  endSession as endSessionApi,
  startSession as startSessionApi,
  updateSession as updateSessionApi,
} from '../services/sessionApi';
import { Session } from '../types/types';
import {
  SESSION_KEY,
  LATEST_SESSION_UPDATE_TIME_KEY,
  USAGE_REPORTING_GRACE_PERIOD_MS,
} from './UserAnalyticsContants';

/**
 * Manages session lifecycle by either starting a new session or updating an existing one.
 * If a valid session exists, it will be updated instead of creating a new one.
 */
export async function startSession(token?: string): Promise<Session> {
  const currentSession = getStoredSession();
  const latestUpdate = getLatestSessionUpdateTime();

  // Check both session existence and activity grace period
  if (
    currentSession &&
    latestUpdate &&
    isLatestUpdateWithinGracePeriod(new Date())
  ) {
    return updateSession(currentSession.id);
  }

  // Create and store new session
  const session = await startSessionApi(token);
  storeSessionData(session);
  updateLatestSessionUpdateTime();
  return session;
}

/**
 * Updates an existing session if one exists.
 * This is used to keep the session alive during active usage.
 */
export async function updateSession(sessionId: string): Promise<Session> {
  const session = await updateSessionApi(sessionId);
  storeSessionData(session);
  updateLatestSessionUpdateTime();
  return session;
}

/**
 * Gracefully ends the current session and cleans up local storage.
 * Will attempt to notify the server even if local cleanup fails.
 */
export async function endSession() {
  const currentSession = getStoredSession();
  if (!currentSession) {
    return;
  }

  try {
    /* Remove session data from local storage before API call because the code
    after endSessionApi() might not be executed if the tab is closed or the user
    navigates away from the page. Session data needs to be removed from local
    storage before the API call so that whether or not the API call succeeds,
    the old session data won't be used again and a new session is created next
    time. Otherwise, the old session might be updated after already being ended.
    */
    removeSessionData();

    await endSessionApi(currentSession.id);
  } catch (_) {
    // TODO: Should we log the error somewhere like Sentry?
  }
}

// Local storage management functions
function storeSessionData(session: Session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function removeSessionData() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(LATEST_SESSION_UPDATE_TIME_KEY);
}

export function getStoredSession(): Session | null {
  const session = localStorage.getItem(SESSION_KEY);
  if (!session) {
    return null;
  }

  // Make sure the session is a valid Session object
  try {
    const parsedSession = JSON.parse(session);
    return parsedSession as Session;
  } catch (_) {
    return null;
  }
}

export function getLatestSessionUpdateTime(): Date | null {
  const storedTime = localStorage.getItem(LATEST_SESSION_UPDATE_TIME_KEY);
  if (!storedTime) {
    return null;
  }

  const date = new Date(parseInt(storedTime, 10));
  return isNaN(date.getTime()) ? null : date;
}

function updateLatestSessionUpdateTime(): void {
  localStorage.setItem(LATEST_SESSION_UPDATE_TIME_KEY, Date.now().toString());
}

export function isLatestUpdateWithinGracePeriod(currentTime: Date): boolean {
  const latestUpdate = getLatestSessionUpdateTime();
  if (!latestUpdate) {
    return false;
  }

  return (
    currentTime.getTime() - latestUpdate.getTime() <=
    USAGE_REPORTING_GRACE_PERIOD_MS
  );
}
